import { useState, useEffect } from 'react'
import filesService from '../../../../services/files'
import { analysesApi } from '../../api'
import Button from '../../../../components/common/Button'
import LoadingSpinner from '../../../../components/common/LoadingSpinner'
import Alert from '../../../../components/common/Alert'

/**
 * Preview component for volume figure SVG
 * Step 3: User reviews preview, can adjust or finalize
 */
function VolumeFigurePreview({ analysis, onSubmit, loading = false }) {
  const [svgContent, setSvgContent] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [error, setError] = useState(null)
  const [adjusting, setAdjusting] = useState(false)

  // Get preview file ID from Step 2's output
  const previousStep = analysis.steps?.find((s) => s.step_number === 2)
  const previewFileId = previousStep?.output_data?.preview_file_id

  useEffect(() => {
    const fetchSvg = async () => {
      if (!previewFileId) {
        setError('Preview file not found')
        setLoadingPreview(false)
        return
      }

      try {
        setLoadingPreview(true)
        setError(null)

        // Download SVG file (unencrypted, so we can display inline)
        const response = await filesService.downloadFile(previewFileId)

        // Response should be SVG blob - convert to text
        const svgText = await response.data.text()
        setSvgContent(svgText)
      } catch (err) {
        console.error('Preview load error:', err)
        setError('Failed to load preview')
      } finally {
        setLoadingPreview(false)
      }
    }

    fetchSvg()
  }, [previewFileId])

  const handleAdjust = async () => {
    try {
      setAdjusting(true)
      // Call adjust-input endpoint to reset to Step 1
      await analysesApi.adjustInput(analysis.id)
      // Reload page to show form
      window.location.reload()
    } catch (err) {
      console.error('Adjust error:', err)
      setError(err.response?.data?.error || 'Failed to adjust input')
    } finally {
      setAdjusting(false)
    }
  }

  const handleFinalize = () => {
    // Proceed to step 4 (final generation) - API will wrap with input_data
    onSubmit({ finalized: true })
  }

  if (loadingPreview) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <LoadingSpinner />
          <p className="text-gray-600 mt-4">Loading preview...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return <Alert variant="error" message={error} />
  }

  return (
    <div className="space-y-6">
      {/* Preview Display */}
      <div className="bg-white border border-gray-300 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Preview</h3>

        {/* Inline SVG display */}
        <div
          className="border border-gray-200 rounded-lg p-8 bg-gray-50 overflow-auto"
          style={{ maxHeight: '600px' }}
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      </div>

      {/* Info message */}
      <Alert
        variant="info"
        message={
          <>
            <strong>Review the preview above.</strong> You can adjust inputs and regenerate the
            preview, or finalize to create all file formats (DXF, SVG, PNG) for download.
          </>
        }
      />

      {/* Actions */}
      <div className="flex gap-4">
        <Button
          type="button"
          variant="secondary"
          onClick={handleAdjust}
          loading={adjusting}
          disabled={loading}
        >
          {adjusting ? 'Adjusting...' : 'Adjust Inputs'}
        </Button>

        <Button
          type="button"
          variant="primary"
          onClick={handleFinalize}
          loading={loading}
          disabled={adjusting}
          className="flex-1"
        >
          {loading ? 'Finalizing...' : 'Finalize & Download'}
        </Button>
      </div>
    </div>
  )
}

export default VolumeFigurePreview
